import {Component, OnDestroy, OnInit} from '@angular/core';
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {SpinnerService} from "../../../utils/services/spinner.service";
import {map, Observable, of, switchMap, tap, zip} from "rxjs";
import {SessionInfoService} from "../../../services/session-info.service";
import { FontSizeService } from 'src/app/services/font-size.service';
import {AuthorizationServiceProvider} from "../../../auth_profile/services/authorization-service.provider";
import {AuthorizedUserDetailsService} from "../../../auth_profile/services/api/authorization.api";
import {V3ExamSession, V3ExamSessionPart} from "../../model/v3/cspa";
import {CspaRestV3} from "../../services/v3/cspa-rest-v3";

type ExamPartSessionData = {
  session: V3ExamSession;
  parts: V3ExamSessionPart[];
}

@Component({
    selector: 'app-exam-part-details',
    templateUrl: './exam-part-details.component.html',
    styleUrls: ['./exam-part-details.component.scss'],
    standalone: false
})
export class ExamPartDetailsComponent implements OnInit, OnDestroy {
  uuId: string
  section: string
  chapter: string
  exerciseSet: string
  sessionPartId: number
  session: V3ExamSession
  sessionParts: V3ExamSessionPart[]
  currentPart: V3ExamSessionPart
  private personId?: number

  constructor(
    private provider: NativeServiceApiProvider,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: SpinnerService,
    private progressInfoService: SessionInfoService,
    private auth: AuthorizationServiceProvider,
    public fontSizeService: FontSizeService
  ) { }

  ngOnInit(): void {
    this.route.paramMap.pipe(
      tap((params: ParamMap) => this.readRouteParams(params)),
      switchMap(_ => this.loadData()),
    ).subscribe()
    this.fontSizeService.setFromLocalStorage()
  }

  loadData() {
    return this.spinner.trace(
      this.withApiAndPersonId((api, personId) =>
        this.loadSessionAndParts(api, personId),
      ).pipe(
        tap(sessionData => this.applyLoadedData(sessionData)),
      ),
    )
  }

  ngOnDestroy(){
    this.fontSizeService.setToDefault()
  }

  getAccent() {
    return `accent stg-${this.session?.chapterName.slice(-2).trim()}`
  }

  getExerciseType() {
    return this.session?.sectionName?.split(',')[0]
  }

  getChapter() {
    return this.session?.chapterName
  }

  getExamName() {
    return this.session?.examName
  }

  getExamPartName() {
    return this.currentPart?.partName
  }

  getExerciseSet() {
    return this.exerciseSet
  }

  getCurrentPartNr() {
    return this.sessionParts?.findIndex(it => it.path == this.currentPart?.path) + 1
  }

  goToExerciseSetsWithFilter() {
    this.router.navigate(
      ['../../../../../../'],
      {
        relativeTo: this.route,
        queryParams: { language: `${this.exerciseSet}`},
        queryParamsHandling: 'merge'
      }
    ).then()
  }

  finishSession() {
    return this.withApiAndPersonId((api, personId) =>
      this.finishCurrentPartAndAdvanceExam(api, personId).pipe(
        switchMap(() => api.getExamSession(personId, this.uuId)),
      ),
    )
  }

  goBack() {
    this.finishSession().subscribe(_ => {
      this.navigateRelative(['../../'])
    });
  }

  isSampleExam() {
    const sampleTag = this.session?.examName?.split(' ')[1]
    return sampleTag === '0'
  }

  private readRouteParams(params: ParamMap) {
    this.uuId = params.get("sessionId")
    this.section = params.get("section");
    this.chapter = params.get("chapter");
    this.exerciseSet = params.get("exerciseSet");
    this.sessionPartId = +params.get("sessionPartId") - 1;
  }

  private applyLoadedData(sessionData: ExamPartSessionData) {
    this.session = sessionData.session
    this.sessionParts = sessionData.parts
    this.currentPart = this.resolveCurrentPart(sessionData.session, sessionData.parts)
    this.progressInfoService.setSessionInfo({
      questionNb: "1",
      session: this.session,
      questionType: 'exams',
    })
  }

  private getPersonId(authDetailsApi: AuthorizedUserDetailsService) {
    if (this.personId != null) {
      return of(this.personId);
    }
    return authDetailsApi.getSelfPersonId().pipe(
      tap(personId => this.personId = personId),
    );
  }

  private resolvePersonId() {
    if (this.personId != null) {
      return of(this.personId);
    }
    return this.auth.getAuthDetailsService().pipe(
      switchMap(authDetailsApi => this.getPersonId(authDetailsApi)),
    );
  }

  private resolveCurrentPart(session: V3ExamSession, parts: V3ExamSessionPart[]) {
    if (parts.length === 0) {
      return null;
    }
    if (this.sessionPartId >= 0 && this.sessionPartId < parts.length) {
      return parts[this.sessionPartId];
    }
    if (session.currentPart?.num != null) {
      return parts.find(part => part.num === session.currentPart.num) ?? parts[0];
    }
    if (session.currentPart?.path) {
      return parts.find(part => part.path === session.currentPart.path) ?? parts[0];
    }
    return parts[0];
  }

  private getCurrentPartNum() {
    return this.currentPart?.num ?? this.sessionPartId + 1;
  }

  private withApiAndPersonId<T>(action: (api: CspaRestV3, personId: number) => Observable<T>) {
    return this.provider.cspaV3().pipe(
      switchMap(api =>
        this.resolvePersonId().pipe(
          switchMap(personId => action(api, personId)),
        ),
      ),
    )
  }

  private loadSessionAndParts(api: CspaRestV3, personId: number) {
    return zip(
      api.getExamSession(personId, this.uuId),
      api.findExamSessionsParts(personId, this.uuId),
    ).pipe(
      map(([session, parts]) => ({
        session,
        parts,
      } as ExamPartSessionData)),
    )
  }

  private finishCurrentPartAndAdvanceExam(api: CspaRestV3, personId: number) {
    const currentPartNum = this.getCurrentPartNum()
    const nextPartNum = currentPartNum + 1
    const hasNextPart = this.sessionParts?.some(part => part.num === nextPartNum)

    return api.updatePartStatus(personId, this.uuId, currentPartNum, 'FINISHED').pipe(
      switchMap(() =>
        hasNextPart
          ? api.updatePartStatus(personId, this.uuId, nextPartNum, 'OPEN')
          : api.updateExamSessionStatus(personId, this.uuId, 'FINISHED'),
      ),
    )
  }

  private navigateRelative(path: string[]) {
    this.router.navigate(
      path,
      {
        relativeTo: this.route,
        queryParamsHandling: 'merge'
      }
    ).then()
  }
}
